import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faTiktok, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faArrowLeft, faArrowRight, faVideo } from '@fortawesome/free-solid-svg-icons';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import Games from './Games'; // Make sure this import is correct
import './css/App.css';

// Import your images
import turkeyRunImage from './imgs/goose-8740266_1280.jpg';
import shadowsImage from './imgs/knight-7169531_1280.jpg';
import loveLetterImage from './imgs/sculpture-3448975_1280.jpg';
import SnakeGame from './other pages/Snake';
import BubbleShooter from './other pages/BubbleShooter';
import FlappyBird from './other pages/FlappyBird';
import Puzzle2048 from './other pages/Puzzle2048';
import Chess  from './other pages/Chess';
import TicTacToePage from './other pages/TicTacToe';
import MemoryMatchPage from './other pages/MemoryMatch';
import BreakoutPage from './other pages/Breakout';
import HangmanPage from './other pages/Hangman';
import EatYourFoodPage from './other pages/EatYourFood';

// Define types for our data
interface Game {
  id: number;
  title: string;
  description: string;
  image: string;
  status: string;
}

const App: React.FC = () => {
  const location = useLocation();
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentGame, setCurrentGame] = useState<Game | null>(null);
  const isStandaloneGamesSite = location.pathname.startsWith('/play/');

  const games: Game[] = [
    {
      id: 1,
      title: "Turkey Run",
      description: "You're a turkey and it's Thanksgiving! Escape from humans and predators! Or else you'll be dinner!",
      image: turkeyRunImage,
      status: "coming soon"
    },
    {
      id: 2,
      title: "Shadows of all Hallows",
      description: "The history of Halloween is reimagined with this fun game! Become the knight the town needs!",
      image: shadowsImage,
      status: "coming soon"
    },
    {
      id: 3,
      title: "Love Letter",
      description: "It's Valentine's day and Cupid has a crush. Help him deliver the citizens' letters to their loved ones before time runs out!",
      image: loveLetterImage,
      status: "coming soon"
    }
  ];

  // Auto-advance slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % games.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [games.length]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % games.length);
  }, [games.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + games.length) % games.length);
  }, [games.length]);

  const openModal = useCallback((game: Game) => {
    setCurrentGame(game);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setCurrentGame(null);
  }, []);

  return (
    <div className="App">
      {!isStandaloneGamesSite && <Header />}

      {/* Add Routes to handle navigation */}
      <Routes>
        <Route path="/" element={
          <>
            <div className="slider">
              <div className="list">
                {games.map((game, index) => (
                  <div
                    key={game.id}
                    className={`item ${index === currentSlide ? 'active' : ''}`}
                  >
                    <img src={game.image} alt={game.title} />
                    <div className="content">
                      <p>{game.status}</p>
                      <h2>{game.title}</h2>
                      <p>{game.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="arrows">
                <button id="prev" title="prev" onClick={prevSlide}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <button id="next" title="next" onClick={nextSlide}>
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>

              <div className="thumbnail">
                {games.map((game, index) => (
                  <div
                    key={game.id}
                    className={`item ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => setCurrentSlide(index)}
                  >
                    <img src={game.image} alt={game.title} />
                    <div className="content">
                      <p className="game-name">{game.title}</p>
                      <div className="button-row">
                        <a href="/"
                          className="watch-trailer"
                          aria-label="trailer"
                          onClick={(e) => {
                            e.preventDefault();
                            openModal(game);
                          }}
                        >
                          <FontAwesomeIcon icon={faVideo} />
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {isModalOpen && currentGame && (
              <GameModal game={currentGame} onClose={closeModal} />
            )}
          </>
        } />
        <Route path="/games" element={<Navigate to="/play" replace />} />
        <Route path="/play" element={<Games />} />
        <Route path="/play/snake" element={<SnakeGame />} />
        <Route path="/play/2048" element={<Puzzle2048 />} />
        <Route path="/play/flappy-bird" element={<FlappyBird />} />
        <Route path="/play/bubble-shooter" element={<BubbleShooter />} />
        <Route path="/play/chess" element={<Chess />} />
        <Route path="/play/tic-tac-toe" element={<TicTacToePage />} />
        <Route path="/play/memory-match" element={<MemoryMatchPage />} />
        <Route path="/play/breakout" element={<BreakoutPage />} />
        <Route path="/play/hangman" element={<HangmanPage />} />
        <Route path="/play/eat-your-food" element={<EatYourFoodPage />} />
        <Route path="/snake" element={<Navigate to="/play/snake" replace />} />
        <Route path="/2048" element={<Navigate to="/play/2048" replace />} />
        <Route path="/flappy-bird" element={<Navigate to="/play/flappy-bird" replace />} />
        <Route path="/bubble-shooter" element={<Navigate to="/play/bubble-shooter" replace />} />
        <Route path="/chess" element={<Navigate to="/play/chess" replace />} />
        <Route path="/tic-tac-toe" element={<Navigate to="/play/tic-tac-toe" replace />} />
        <Route path="/memory-match" element={<Navigate to="/play/memory-match" replace />} />
        <Route path="/breakout" element={<Navigate to="/play/breakout" replace />} />
        <Route path="/hangman" element={<Navigate to="/play/hangman" replace />} />
        <Route path="/eat-your-food" element={<Navigate to="/play/eat-your-food" replace />} />
        <Route path="*" element={<main className="search-filter-section"><h1>Page not found</h1><p>This address does not match a page on Ann Studios.</p><Link className="play-button" to="/play">Browse games</Link></main>} />
        
      </Routes>

      <GoogleAnalytics />
    </div>
  );
};

const Header: React.FC = () => {
  const location = useLocation();
  return (
    <header className={location.pathname === '/' ? 'home-header' : undefined}>
      <div className="logo">
        <Link to="/">Ann Studios</Link>
      </div>
      <nav className="gametab" aria-label="Main navigation">
        <Link to="/games">Games</Link>
      </nav>
      <div className="social-icons-sidebar">
        <a href="https://facebook.com" rel="noopener noreferrer" title="Facebook" target="_blank">
          <FontAwesomeIcon icon={faFacebookF} />
        </a>
        <a href="https://tiktok.com" rel="noopener noreferrer" title="Tiktok" target="_blank">
          <FontAwesomeIcon icon={faTiktok} />
        </a>
        <a href="https://instagram.com" rel="noopener noreferrer" title="Instagram" target="_blank">
          <FontAwesomeIcon icon={faInstagram} />
        </a>
      </div>
    </header>
  );
};

interface GameModalProps {
  game: Game;
  onClose: () => void;
}

const GameModal: React.FC<GameModalProps> = ({ game, onClose }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>
          &times;
        </button>
        <h2>{game.title}</h2>
        <p>{game.description}</p>
        <div className="modal-actions">
          <button className="btn">Watch Trailer</button>
          <button className="btn">Learn More</button>
        </div>
      </div>
    </div>
  );
};

const GoogleAnalytics: React.FC = () => {
  useEffect(() => {
    // Google Analytics script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=AW-16687754010';
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'AW-16687754010');
      gtag('event', 'conversion', {
        'send_to': 'AW-16687754010/g2C0CIH9ydEZEJrWq5U-'
      });
    `;
    document.head.appendChild(script2);

    // Google Tag Manager script
    const script3 = document.createElement('script');
    script3.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-PF6M4VXH');
    `;
    document.head.appendChild(script3);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
      document.head.removeChild(script3);
    };
  }, []);

  return (
    <>
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-KXT8PGL6"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
          title="Google Tag Manager"
        ></iframe>
      </noscript>
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-PF6M4VXH"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
          title="Google Tag Manager"
        ></iframe>
      </noscript>
    </>
  );
};

export default App;
